import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import IconButton from './IconButton';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: string;
  width?: 'md' | 'lg' | 'xl';
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const widthMap: Record<NonNullable<DrawerProps['width']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-3xl',
};

export default function Drawer({
  isOpen,
  onClose,
  title,
  subtitle,
  width = 'lg',
  footer,
  children,
}: DrawerProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    const t = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80]">
      <div
        className={`absolute inset-0 bg-gray-950/60 backdrop-blur-sm transition-opacity duration-moderate ease-out-expo ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`absolute right-0 top-0 h-full w-screen ${widthMap[width]} bg-gray-900 border-l border-gray-700/60 shadow-overlay flex flex-col transition-transform duration-moderate ease-out-expo ${
          visible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-gray-700/60">
          <div className="min-w-0">
            {typeof title === 'string' ? (
              <h2 className="text-h3 font-semibold text-gray-50 font-display truncate">{title}</h2>
            ) : (
              title
            )}
            {subtitle && <p className="text-meta text-gray-400 mt-1 truncate">{subtitle}</p>}
          </div>
          <IconButton
            icon={X}
            aria-label="Cerrar"
            variant="ghost"
            onClick={onClose}
            className="flex-shrink-0 -mr-1"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">{children}</div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-700/60 px-6 py-4 bg-gray-900/80">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
